import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Folder, FolderPlus, Upload, File as FileIcon, Download, Share2, Trash2, X,
  ChevronRight, Home, RefreshCw, Link2, Copy, Check,
} from 'lucide-react';
import { documentsApi, DocListing, DocFile } from '../services/api';
import { useToast } from '../components/Toast/Toast';

const fmtSize = (n: number) => {
  const b = Number(n) || 0;
  if (b >= 1024 * 1024 * 1024) return `${(b / 1024 / 1024 / 1024).toFixed(1)} GB`;
  if (b >= 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${b} B`;
};
const fmtDate = (s: any) => { const d = new Date(String(s).replace(' ', 'T')); return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }); };

type ShareTarget = { type: 'file' | 'folder'; id: number; name: string };

const EXPIRY_OPTIONS = [
  { label: '1 hour', hours: 1 },
  { label: '1 day', hours: 24 },
  { label: '7 days', hours: 24 * 7 },
  { label: '30 days', hours: 24 * 30 },
  { label: 'Never', hours: 0 },
];

export default function Documents() {
  const { showSuccess, showError } = useToast();
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [listing, setListing] = useState<DocListing | null>(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [shareTarget, setShareTarget] = useState<ShareTarget | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await documentsApi.list(currentId);
      if (r.success) setListing(r.data);
    } catch (e: any) { showError('Error', e?.message || 'Failed to load documents'); }
    finally { setLoading(false); }
  }, [currentId, showError]);

  useEffect(() => { load(); }, [load]);

  const doUpload = async (files: File[]) => {
    if (!files.length) return;
    setUploadPct(0);
    try {
      await documentsApi.upload(currentId, files, pct => setUploadPct(pct));
      showSuccess('Uploaded', `${files.length} file(s) uploaded`);
      load();
    } catch (e: any) { showError('Upload failed', e?.message || 'Could not upload'); }
    finally { setUploadPct(null); }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) doUpload(files);
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await documentsApi.createFolder(name, currentId);
      setShowNewFolder(false); setNewFolderName('');
      showSuccess('Created', `Folder "${name}" created`);
      load();
    } catch (e: any) { showError('Error', e?.message || 'Could not create folder'); }
  };

  const deleteFolder = async (id: number, name: string) => {
    if (!window.confirm(`Delete folder "${name}" and ALL its contents? This cannot be undone.`)) return;
    try { await documentsApi.deleteFolder(id); showSuccess('Deleted', 'Folder deleted'); load(); }
    catch (e: any) { showError('Error', e?.message || 'Could not delete'); }
  };

  const deleteFile = async (f: DocFile) => {
    if (!window.confirm(`Delete "${f.original_name}"?`)) return;
    try { await documentsApi.deleteFile(f.id); showSuccess('Deleted', 'File deleted'); load(); }
    catch (e: any) { showError('Error', e?.message || 'Could not delete'); }
  };

  const download = async (f: DocFile) => {
    try { await documentsApi.download(f.id, f.original_name); }
    catch (e: any) { showError('Download failed', e?.message || 'Could not download'); }
  };

  return (
    <div className="p-3 sm:p-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Folder className="h-5 w-5 text-blue-600" />
          <h1 className="text-lg font-bold text-gray-800">Documents</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowNewFolder(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-blue-300 rounded text-[13px] text-blue-700 bg-blue-50 hover:bg-blue-100">
            <FolderPlus size={15} /> New Folder
          </button>
          <button onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[13px] text-white bg-blue-600 hover:bg-blue-700">
            <Upload size={15} /> Upload
          </button>
          <input ref={fileInputRef} type="file" multiple className="hidden"
            onChange={e => { const fs = Array.from(e.target.files || []); if (fs.length) doUpload(fs); e.currentTarget.value = ''; }} />
          <button onClick={load} className="p-2 rounded text-slate-500 hover:bg-slate-100" title="Refresh"><RefreshCw size={15} className={loading ? 'animate-spin' : ''} /></button>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="flex items-center gap-1 text-[13px] text-gray-600 mb-3 flex-wrap">
        <button onClick={() => setCurrentId(null)} className="inline-flex items-center gap-1 hover:text-blue-700"><Home size={14} /> Home</button>
        {listing?.breadcrumb.map(b => (
          <span key={b.id} className="inline-flex items-center gap-1">
            <ChevronRight size={13} className="text-gray-400" />
            <button onClick={() => setCurrentId(b.id)} className={`hover:text-blue-700 ${b.id === currentId ? 'font-semibold text-gray-800' : ''}`}>{b.name}</button>
          </span>
        ))}
      </div>

      {/* Drop zone + content */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-lg bg-white min-h-[55vh] transition-colors ${dragOver ? 'border-blue-500 bg-blue-50/50' : 'border-gray-200'}`}
      >
        {uploadPct !== null && (
          <div className="px-4 pt-3">
            <div className="flex items-center justify-between text-[12px] text-blue-700 mb-1"><span>Uploading…</span><span>{uploadPct}%</span></div>
            <div className="h-1.5 bg-blue-100 rounded-full overflow-hidden"><div className="h-full bg-blue-600 transition-all" style={{ width: `${uploadPct}%` }} /></div>
          </div>
        )}

        {loading ? (
          <div className="text-center text-gray-400 py-20 text-sm">Loading…</div>
        ) : (listing && (listing.folders.length > 0 || listing.files.length > 0)) ? (
          <div className="p-3">
            {/* Folders */}
            {listing.folders.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-3">
                {listing.folders.map(f => (
                  <div key={f.id} className="group flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2.5 hover:border-blue-300 hover:bg-blue-50/40 cursor-pointer"
                    onClick={() => setCurrentId(f.id)}>
                    <Folder className="h-6 w-6 text-amber-500 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-gray-800 truncate">{f.name}</div>
                      <div className="text-[10px] text-gray-400">{f.subfolder_count} folder(s) · {f.file_count} file(s)</div>
                    </div>
                    <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100" onClick={e => e.stopPropagation()}>
                      <button onClick={() => setShareTarget({ type: 'folder', id: f.id, name: f.name })} className="text-gray-300 hover:text-emerald-600" title="Share folder (zip)"><Share2 size={15} /></button>
                      <button onClick={() => deleteFolder(f.id, f.name)} className="text-gray-300 hover:text-red-500" title="Delete folder"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Files */}
            {listing.files.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-[13px]">
                  <thead className="bg-slate-50 text-[11px] text-gray-500 uppercase">
                    <tr>
                      <th className="text-left px-3 py-2 font-bold">Name</th>
                      <th className="text-right px-3 py-2 font-bold w-[90px]">Size</th>
                      <th className="text-left px-3 py-2 font-bold w-[110px] hidden sm:table-cell">Added</th>
                      <th className="text-center px-3 py-2 font-bold w-[130px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listing.files.map(f => (
                      <tr key={f.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-800 truncate max-w-[320px]">
                          <span className="inline-flex items-center gap-2"><FileIcon size={15} className="text-gray-400 shrink-0" />{f.original_name}</span>
                        </td>
                        <td className="px-3 py-2 text-right text-gray-500 tabular-nums">{fmtSize(f.size)}</td>
                        <td className="px-3 py-2 text-gray-500 hidden sm:table-cell">{fmtDate(f.created_at)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-2.5">
                            <button onClick={() => download(f)} className="text-blue-600 hover:text-blue-800" title="Download"><Download size={16} /></button>
                            <button onClick={() => setShareTarget({ type: 'file', id: f.id, name: f.original_name })} className="text-emerald-600 hover:text-emerald-800" title="Share link"><Share2 size={16} /></button>
                            <button onClick={() => deleteFile(f)} className="text-red-500 hover:text-red-700" title="Delete"><Trash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-center text-gray-400 py-24 px-4">
            <Upload className="h-10 w-10 mb-3 text-gray-300" />
            <div className="text-sm font-medium text-gray-500">Drag &amp; drop files here</div>
            <div className="text-[12px] mt-1">or use the Upload button · create a New Folder to organize</div>
          </div>
        )}
      </div>

      {/* New folder modal */}
      {showNewFolder && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowNewFolder(false)}>
          <div className="bg-white rounded-lg shadow-xl p-5 w-[380px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800">New Folder</h3>
              <button onClick={() => setShowNewFolder(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <input autoFocus value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') createFolder(); }}
              placeholder="Folder name" className="w-full border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-blue-400 mb-3" />
            <button onClick={createFolder} className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded py-2">Create</button>
          </div>
        </div>
      )}

      {/* Share modal */}
      {shareTarget && <ShareModal target={shareTarget} onClose={() => setShareTarget(null)} />}
    </div>
  );
}

function ShareModal({ target, onClose }: { target: ShareTarget; onClose: () => void }) {
  const { showError } = useToast();
  const isFolder = target.type === 'folder';
  const [hours, setHours] = useState<number>(24);
  const [link, setLink] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const r = isFolder
        ? await documentsApi.shareFolder(target.id, hours || null)
        : await documentsApi.share(target.id, hours || null);
      if (r.success) {
        // Build the public link from the BROWSER's origin (the real domain the
        // app is on), not the backend's reported host — behind a reverse proxy
        // that host is the internal 127.0.0.1:5000, which no recipient can reach.
        setLink(`${window.location.origin}${r.data.path}`);
        setExpiresAt(r.data.expires_at);
      }
    } catch (e: any) { showError('Error', e?.message || 'Could not create link'); }
    finally { setBusy(false); }
  };

  const copy = () => {
    if (!link) return;
    navigator.clipboard.writeText(link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl p-5 w-[440px] max-w-[95vw]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-800 inline-flex items-center gap-2"><Link2 size={16} className="text-emerald-600" /> Share {isFolder ? 'folder' : 'link'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="text-[13px] text-gray-500 mb-3 truncate inline-flex items-center gap-1.5">
          {isFolder ? <Folder size={14} className="text-amber-500" /> : <FileIcon size={14} className="text-gray-400" />}
          {target.name}{isFolder ? ' (zip)' : ''}
        </div>

        {!link ? (
          <>
            <label className="block text-[12px] font-semibold text-gray-500 mb-1">Link expires after</label>
            <div className="grid grid-cols-3 gap-1.5 mb-4">
              {EXPIRY_OPTIONS.map(o => (
                <button key={o.hours} onClick={() => setHours(o.hours)}
                  className={`py-1.5 rounded text-[12px] font-medium border ${hours === o.hours ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                  {o.label}
                </button>
              ))}
            </div>
            <button onClick={generate} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white text-sm font-medium rounded py-2">
              {busy ? 'Generating…' : 'Generate link'}
            </button>
            <p className="text-[11px] text-gray-400 mt-2">Anyone with the link can download this {isFolder ? 'folder as a .zip' : 'file'} until it expires — no login needed.</p>
          </>
        ) : (
          <>
            <div className="flex items-stretch gap-2 mb-2">
              <input readOnly value={link} className="flex-1 border border-gray-300 rounded px-2 py-2 text-[12px] bg-gray-50 outline-none" onFocus={e => e.currentTarget.select()} />
              <button onClick={copy} className="px-3 rounded bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-1 text-[13px]">
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>
            <p className="text-[12px] text-gray-500 mb-3">{expiresAt ? `Expires: ${new Date(expiresAt + 'Z').toLocaleString('en-GB')}` : 'Never expires'}</p>
            <div className="flex gap-2">
              <a href={`https://wa.me/?text=${encodeURIComponent(link)}`} target="_blank" rel="noreferrer" className="flex-1 text-center py-2 rounded border border-green-300 text-green-700 bg-green-50 hover:bg-green-100 text-[13px] font-medium">WhatsApp</a>
              <a href={`mailto:?subject=${encodeURIComponent(`Shared ${isFolder ? 'folder' : 'file'}: ${target.name}`)}&body=${encodeURIComponent(link)}`} className="flex-1 text-center py-2 rounded border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 text-[13px] font-medium">Email</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
